/**
 * WorkflowOffre.js
 *
 * Workflow de transmission et d'acceptation d'une offre d'indemnisation.
 *
 * Phases (par offre/victime) :
 *   1 → Envoi au bureau homologue / assureur togolais  (mail ou postal)
 *   2 → Réponse homologue  : validation directe  OU  contre-offre chiffrée
 *   3 → Transmission à la victime  (offre ou contre-offre)
 *   4 → Décision victime  : accord (upload GED + BAP sous-dossier)  OU  rejet
 *
 * L'état est persisté via PATCH sur l'offre (backend doit exposer les routes).
 * Le statut sinistre est mis à jour automatiquement à chaque étape clé.
 */

import React, { useState, useEffect } from 'react';
import {
  Steps, Button, Modal, Form, Input, Select,
  DatePicker, Space, Tag, Alert, Divider, Spin, Tooltip,
  Row, Col,
} from 'antd';
import {
  SendOutlined, DownloadOutlined, MailOutlined,
  CheckCircleOutlined, CloseCircleOutlined,
  SwapOutlined, SyncOutlined, UserOutlined,
  ExclamationCircleOutlined, SafetyOutlined, WalletOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { imprimerDocument, imprimerPdf } from '../../../utils/printIframe';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import axiosInstance from '../../../variables/api/axiosInstance';
import CBInputNumber from '../../../components/CBInputNumber';
import GedFileField from '../../../components/GedFileField';
import { formatMontant, estVictimeIndemnisable } from '../../../variables/constants';
import { partieGarantie, adresseGarantie } from '../../../services/partieGarantie';
import logoSrc from '../../../assets/images/logo.png';

import i18n from '../../../i18n';
import {
  formatMontantDocument, langueDocument, langueVictime, tDocument, useLangueDocument,
} from '../../../i18n/documents';

const G = '#1a6b3c';
const PDF_HEADER_H = 24; // mm reserves en haut
const PDF_FOOTER_H = 10; // mm reserves en bas

/* METRIQUE DU DOCUMENT, REGROUPEE ICI.
   L'offre doit tenir sur une A4 pour le cas courant. Ces valeurs etaient
   dispersees dans la fonction de generation, ce qui rendait tout reglage de
   densite hasardeux : il fallait retrouver six tailles de police et cinq
   marges de cellule sans en oublier, et un seul oubli suffisait a decaler une
   section. Reunies, la densite se regle en une ligne.

   CE QUI N'EST PAS GARANTI. Un dossier charge - un deces avec plusieurs ayants
   droit et de nombreux chefs de prejudice - passera sur une seconde page :
   aucune police ne fait entrer un contenu variable dans une hauteur fixe. Ce
   qui EST garanti, c'est que les signatures ne partiront jamais seules, par le
   saut de page conditionnel pose avant leur bloc. */
const PDF_FS_TABLE = 7.6;
const PDF_FS_SECTION = 8.2;
const PDF_CELL_PAD = 1.15;
const PDF_GAP_TITRE = 3.2;   // entre un intitule de section et son tableau
const PDF_GAP_SECTION = 2.2; // entre deux sections

// Pre-load logo once at module init
let _logoDataUrl = null;
fetch(logoSrc)
  .then(r => r.blob())
  .then(b => { const rd = new FileReader(); rd.onload = () => { _logoDataUrl = rd.result; }; rd.readAsDataURL(b); })
  .catch(() => { });

function drawPdfHeader(doc, ML, MR) {
  const GB = [26, 107, 60];
  const CW = MR - ML;
  const colW = CW / 3;  // each third
  // 17 et non 20 : l'en-tete est passe de 28 a 24 mm, et l'embleme plus sa
  // legende doivent tenir AU-DESSUS du filet, pas dessus.
  const logoSize = 17;
  const logoX = ML + colW + (colW - logoSize) / 2; // centered in middle third
  const logoY = 3;
  // Top accent bar
  doc.setFillColor(...GB);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 1.5, 'F');
  // Logo (center column)
  if (_logoDataUrl) {
    doc.addImage(_logoDataUrl, 'PNG', logoX, logoY, logoSize, logoSize);
  }
  // Caption under logo
  doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(140, 140, 140);
  doc.text('Carte Brune CEDEAO', ML + colW + colW / 2, logoY + logoSize + 2.5, { align: 'center' });
  // French title (left column, centered)
  const frCx = ML + colW / 2;
  doc.setFontSize(9.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
  doc.text('BUREAU NATIONAL TOGOLAIS', frCx, 9, { align: 'center' });
  doc.text('CARTE BRUNE CEDEAO', frCx, 14, { align: 'center' });
  // English title (right column, centered)
  const enCx = ML + 2 * colW + colW / 2;
  doc.text('BUREAU NATIONAL TOGOLAIS', enCx, 9, { align: 'center' });
  doc.text('ECOWAS BROWN CARD', enCx, 14, { align: 'center' });
  // Separator line
  doc.setDrawColor(...GB); doc.setLineWidth(0.4);
  doc.line(ML, PDF_HEADER_H, MR, PDF_HEADER_H);
  doc.setTextColor(0, 0, 0);
}

function drawPdfFooter(doc, ML, MR, pageNum, totalPages) {
  const pH = doc.internal.pageSize.getHeight();
  doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.3);
  doc.line(ML, pH - PDF_FOOTER_H + 1, MR, pH - PDF_FOOTER_H + 1);
  doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(150, 150, 150);
  doc.text(
    '290 Rue AGP face ecole EPIC Agbalepedo - 01 BP 2689  Tel: 00228 22258232 - GSM 00228 93002332  Lome-TOGO     Email: tgocartebrune@cartebrune.org',
    (ML + MR) / 2, pH - PDF_FOOTER_H + 5, { align: 'center' },
  );
  doc.setFontSize(7.5);
  doc.text(`${pageNum} / ${totalPages}`, MR, pH - PDF_FOOTER_H + 5, { align: 'right' });
  doc.setTextColor(0, 0, 0);
}


/* ─────────────────────────────────────────────────────────────────────────────
   Détermine la phase courante à partir des champs de l'offre
   Phase -1 = rejet, 0 = non validée, 1-5 = en cours / terminé
───────────────────────────────────────────────────────────────────────────── */
export const getPhaseOffre = (offre, te = false) => {
  if (!offre?.dateValidation) return 0;
  if (!offre?.dateEnvoiHomologue) return 1;
  if (!offre?.dateReponseHomologue) return 2;
  if (!offre?.dateEnvoiVictime) return 3;
  if (!offre?.dateAccordVictime && !offre?.dateRejetVictime) return 4;
  if (offre?.dateRejetVictime) return -1;
  if (!te) return 5;
  /* TE, après l'accord : la compagnie paie par chèque, le BNCB le transmet à
     l'homologue, qui renvoie la quittance. */
  if (!offre?.dateChequeCompagnie) return 6;
  if (!offre?.dateTransmissionCheque) return 7;
  if (!offre?.dateQuittanceHomologue) return 8;
  return 9;
};

const echapperHtml = (t) => String(t ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/* Le courrier imprimé comme une lettre : en-tête, date, destinataire, objet, dans la
   langue du destinataire. `corps` est déjà du HTML (gabarit) ou du texte échappé. */
export function htmlLettreCourriel({ destinataire, sujet, corps, sinistre, titreDocument, langue = 'fr' }) {
  const d = tDocument(langue);
  return `<!DOCTYPE html><html lang="${langue === 'en' ? 'en' : 'fr'}"><head><meta charset="UTF-8"><title>${echapperHtml(sujet || titreDocument)}</title>
      <style>
        @page{size:A4;margin:18mm 18mm 16mm}
        body{font-family:Arial,sans-serif;font-size:12.5px;color:#111;line-height:1.55;margin:0}
        .entete{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:2px solid #1a6b3c;padding-bottom:8px;margin-bottom:22px}
        .entete .org{font-weight:800;font-size:13px;color:#1a6b3c}.entete .sous{font-size:10.5px;color:#6b7280}
        .meta{margin-bottom:18px;font-size:12px}.meta div{margin-bottom:3px}.meta b{display:inline-block;min-width:90px;color:#374151}
        .objet{font-weight:700;margin:0 0 16px}
        .pied{position:fixed;bottom:0;left:0;right:0;border-top:1px solid #e5e7eb;padding-top:4px;font-size:8.5px;color:#9ca3af;text-align:center}
      </style></head><body>
      <div class="entete"><div><div class="org">${d('offre.lettre.entete')}</div><div class="sous">${d('offre.lettre.lomeLe', { date: dayjs().format('DD/MM/YYYY') })}</div></div></div>
      <div class="meta">
        ${destinataire ? `<div><b>${d('offre.lettre.destinataire')}</b> ${echapperHtml(destinataire)}</div>` : ''}
        ${sinistre?.numeroSinistreLocal ? `<div><b>${d('offre.lettre.sinistre')}</b> ${echapperHtml(sinistre.numeroSinistreLocal)}</div>` : ''}
      </div>
      ${sujet ? `<p class="objet">${echapperHtml(d('offre.lettre.objet', { sujet }))}</p>` : ''}
      ${corps}
      <div class="pied">${d('offre.lettre.pied')}</div>
      </body></html>`;
}

/* ─────────────────────────────────────────────────────────────────────────────
   Modal réutilisable d'envoi de mail
───────────────────────────────────────────────────────────────────────────── */
function ModalEnvoyerMail({
  open, onClose, onSent,
  titre, destinataireEmail, sinistre, nature, loading: extLoading,
  documentBlob, nomFichier, sujetInitial, corpsInitial,
  // Titre de la lettre imprimee, a defaut d'objet : dans la langue de la lettre.
  // `titre`, lui, est l'en-tete de la fenetre et suit la langue de l'ecran.
  titreDocument,
  // Langue du destinataire : celle de la lettre imprimee (le sujet et le corps
  // arrivent deja rediges dans cette langue).
  langue = 'fr',
  // Partenaire destinataire et réponse attendue : l'envoi figure dans les états
  // « envoyé à / en attente de » la compagnie ou le bureau homologue.
  destinataireOrganismeTrackingId = null, reponseAttendue = false,
}) {
  const { t } = useTranslation('dossierFinance');
  const [form] = Form.useForm();
  const [templates, setTemplates] = useState([]);
  const [tplSel, setTplSel] = useState(null);
  const [preview, setPreview] = useState('');
  const [loadingTpl, setLoadingTpl] = useState(false);
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState('libre');

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      destinataire: destinataireEmail || '',
      sujet: sujetInitial || '',
      corps: corpsInitial || '',
    });
    axiosInstance.get('/v1/messagerie/templates')
      .then(r => setTemplates(r.data?.data || []))
      .catch(() => { });
  }, [open, destinataireEmail, sujetInitial, corpsInitial, form]);

  const chargerPreview = async (tId) => {
    if (!tId) { setPreview(''); return; }
    setLoadingTpl(true);
    try {
      const r = await axiosInstance.post('/v1/messagerie/templates/preview', {
        templateTrackingId: tId,
        sinistreTrackingId: sinistre?.sinistreTrackingId || null,
      });
      setPreview(r.data?.data?.corpsHtml || '');
      form.setFieldsValue({ sujet: r.data?.data?.sujet || form.getFieldValue('sujet') });
    } catch { toast.error(t('workflow.mail.erreurTemplate')); }
    setLoadingTpl(false);
  };

  const envoyer = async () => {
    try { await form.validateFields(); } catch { return; }
    const vals = form.getFieldsValue();
    setSending(true);
    try {
      const corpsHtml = mode === 'template' ? preview : (vals.corps || '');
      const fd = new FormData();
      fd.append('data', new Blob([JSON.stringify({
        destinataire: vals.destinataire,
        sujet: vals.sujet,
        corpsHtml,
        sinistreTrackingId: sinistre?.sinistreTrackingId || null,
        nature,
        templateTrackingId: tplSel || null,
        destinataireOrganismeTrackingId,
        reponseAttendue,
      })], { type: 'application/json' }));
      if (documentBlob) {
        fd.append('fichiers', documentBlob, nomFichier || 'Offre_indemnisation.pdf');
      }
      await axiosInstance.post('/v1/messagerie/envoyer', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(t('workflow.mail.envoye'));
      form.resetFields();
      setPreview(''); setTplSel(null);
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('workflow.mail.erreurEnvoi'));
    } finally { setSending(false); }
  };

  /* Imprime le courrier comme une lettre : en-tête, date, destinataire, objet. Le
     message libre est du texte : échappé, ses retours à la ligne gardés - écrit tel
     quel en HTML, il s'imprimait en un seul bloc. Sans onglet ouvert en pleine page. */
  const imprimer = () => {
    const vals = form.getFieldsValue();
    const corps = mode === 'template'
      ? (preview || '')
      : `<div style="white-space:pre-wrap">${echapperHtml(vals.corps)}</div>`;
    imprimerDocument(htmlLettreCourriel({
      destinataire: vals.destinataire, sujet: vals.sujet, corps, sinistre, titreDocument, langue,
    }));
  };

  return (
    <Modal
      open={open}
      title={<Space><MailOutlined style={{ color: '#3b82f6' }} />{titre}</Space>}
      onCancel={onClose}
      width={700}
      footer={
        <Space>
          <Button onClick={onClose}>{t('workflow.mail.annuler')}</Button>
          <Button icon={<DownloadOutlined />} onClick={imprimer}>{t('workflow.mail.imprimer')}</Button>
          <Button type="primary" icon={<SendOutlined />} loading={sending || extLoading}
            onClick={envoyer}
            style={{ background: '#2563eb', borderColor: '#2563eb' }}>
            {t('workflow.mail.envoyer')}
          </Button>
        </Space>
      }
      destroyOnClose
    >
      <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
        {langue === 'en' && (
          <Alert type="info" showIcon style={{ borderRadius: 8, marginBottom: 12 }}
            message={t('documents:ecran.messageEnAnglais')} />
        )}
        <Form.Item name="destinataire" label={t('workflow.mail.destinataire')}
          rules={[{ required: true, type: 'email', message: t('workflow.mail.emailInvalide') }]}>
          <Input prefix={<MailOutlined style={{ color: '#9ca3af' }} />}
            placeholder={t('workflow.mail.exempleEmail')} />
        </Form.Item>
        <Form.Item name="sujet" label={t('workflow.mail.objet')} rules={[{ required: true }]}>
          <Input />
        </Form.Item>

        {/* Mode libre / template */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {[['libre', t('workflow.mail.messageLibre')], ['template', t('workflow.mail.utiliserTemplate')]].map(([k, l]) => (
            <button key={k} onClick={() => setMode(k)} style={{
              padding: '4px 14px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
              border: `1px solid ${mode === k ? G : '#e5e7eb'}`,
              background: mode === k ? '#f0fdf4' : '#fff',
              color: mode === k ? G : '#6b7280',
              fontWeight: mode === k ? 600 : 400,
            }}>{l}</button>
          ))}
        </div>

        {mode === 'template' ? (
          <>
            <Form.Item label={t('workflow.mail.templateBncb')}>
              <Select
                placeholder={t('workflow.mail.choisirTemplate')}
                options={templates.map(t => ({
                  value: t.trackingId || t.templateTrackingId,
                  label: t.nom,
                }))}
                value={tplSel}
                onChange={id => { setTplSel(id); chargerPreview(id); }}
                style={{ width: '100%' }}
              />
            </Form.Item>
            {loadingTpl ? <Spin size="small" /> : preview ? (
              <div style={{
                border: '1px solid #e5e7eb', borderRadius: 8, padding: 12,
                maxHeight: 260, overflowY: 'auto', fontSize: 12, background: '#fafafa',
              }} dangerouslySetInnerHTML={{ __html: preview }} />
            ) : null}
          </>
        ) : (
          <Form.Item name="corps" label={t('workflow.mail.corps')}>
            <Input.TextArea rows={6} placeholder={t('workflow.mail.corpsPlaceholder')} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}

/**
 * Sujet et corps pré-remplis du courriel de l'offre, dans la langue du destinataire :
 * `dest` 'homologue' (bureau homologue d'un ET, compagnie d'un TE) ou 'victime'.
 */
export function contenuMailOffre({ dest, isRelance = false, offre, sinistre, victime, langue = 'fr' }) {
  const d = tDocument(langue);
  const te = sinistre?.typeSinistre === 'SURVENU_ETRANGER';
  const montant = (v) => formatMontantDocument(v, langue);
  const numero = sinistre?.referenceSinistreHomologue
    || sinistre?.numeroSinistreHomologue
    || sinistre?.numeroSinistreLocal || '-';
  const nomVictime = `${victime.nom} ${victime.prenoms}`;
  const reference = d('offre.mail.reference', { numero, victime: nomVictime });
  const numeroLocal = sinistre?.numeroSinistreLocal || '-';
  if (dest === 'homologue') {
    const date = offre?.dateEnvoiHomologue
      ? dayjs(offre.dateEnvoiHomologue).format('DD/MM/YYYY') : '-';
    let corps;
    if (isRelance) {
      corps = d(te ? 'offre.mail.corpsCompagnieRelance' : 'offre.mail.corpsHomologueRelance', {
        montant: montant(offre?.montantTotalOffre), date,
      });
    } else if (te) {
      corps = d(offre?.origineOffre === 'HOMOLOGUE' ? 'offre.mail.corpsCompagnieOffreHomologue' : 'offre.mail.corpsCompagnie', {
        homologue: sinistre?.organismeHomologueRaisonSociale || '',
        sinistre: numeroLocal, victime: nomVictime, montant: montant(offre?.montantTotalOffre),
      });
    } else {
      corps = d('offre.mail.corpsHomologue', {
        sinistre: numeroLocal, victime: nomVictime, montant: montant(offre?.montantTotalOffre),
      });
    }
    return { sujet: d(isRelance ? 'offre.mail.sujetHomologueRelance' : 'offre.mail.sujetHomologue', { reference }), corps };
  }
  const montantTransmis = offre?.montantContreOffre ?? offre?.montantTotalOffre;
  const corps = isRelance
    ? d('offre.mail.corpsVictimeRelance', {
      victime: nomVictime, montant: montant(montantTransmis),
      date: offre?.dateEnvoiVictime ? dayjs(offre.dateEnvoiVictime).format('DD/MM/YYYY') : '-',
    })
    : d('offre.mail.corpsVictime', {
      victime: nomVictime, montant: montant(montantTransmis),
      dateAccident: sinistre?.dateAccident ? dayjs(sinistre.dateAccident).format('DD/MM/YYYY') : '-',
    });
  return { sujet: d(isRelance ? 'offre.mail.sujetVictimeRelance' : 'offre.mail.sujetVictime', { reference }), corps };
}

/* ─────────────────────────────────────────────────────────────────────────────
   Upload d'un document d'accord victime dans la GED
───────────────────────────────────────────────────────────────────────────── */
export async function uploadGed(fichier, titre, typeDocument, sinistre, victimeTrackingId) {
  if (!fichier) return null;
  try {
    const fd = new FormData();
    fd.append('file', new Blob([fichier], { type: 'application/pdf' }), fichier.name);
    fd.append('metadata', new Blob([JSON.stringify({
      titre,
      typeDocument,
      sinistreTrackingId: sinistre?.sinistreTrackingId,
      victimeTrackingId: victimeTrackingId ?? null,
    })], { type: 'application/json' }));
    const r = await axiosInstance.post('/v1/ged/documents', fd);
    return r.data?.data?.ossanGedDocumentId || null;
  } catch {
    toast.warning(i18n.t('dossierFinance:workflow.gedNonArchive'));
    return null;
  }
}

/* ─────────────────────────────────────────────────────────────────────────────
   Génération PDF de l'offre - retourne un Blob PDF (impression + pièce jointe)
───────────────────────────────────────────────────────────────────────────── */
const S = (s) => {
  if (s == null) return '';
  return String(s)
    .replace(/-/g, '-').replace(/–/g, '-').replace(/’/g, "'")
    .replace(/°/g, 'o').replace(/•/g, '-').replace(/ /g, ' ');
};

/* Le document parle la langue de son destinataire : celle de la victime, ou celle du
   bureau homologue (ou de la compagnie). Le français est celui d'avant, à l'octet. */

function genererPdfOffre(victime, sinistre, offre, destinataire = 'homologue', expertise = null, ayantsDroit = [], signature = null, langue = 'fr') {
  const d       = tDocument(langue);
  const fmt = v => new Intl.NumberFormat(langue === 'en' ? 'en-GB' : 'fr-FR').format(Math.round(v || 0))
    .replace(/[\u00A0\u202F]/g, '\u0020') + ' FCFA';
  // Lecture locale : new Date('2024-08-17') est lu en UTC et la date reculait d'un jour.
  const fmtDate = d => d ? dayjs(d).format('DD/MM/YYYY') : '-';
  const now = dayjs().format('DD/MM/YYYY');
  const GB = [26, 107, 60];
  const estDecede = victime?.typeVictime === 'DECEDE';
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const ML = 15, MR = W - 15, CW = MR - ML;
  const infoColW = CW * 0.25;
  let y = PDF_HEADER_H + 2;

  // 1) Bandeau titre
  doc.setFillColor(...GB);
  doc.rect(ML, y, CW, 9, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11); doc.setFont('helvetica', 'bold');
  doc.text(S(d('offre.pdf.titre')), ML + CW / 2, y + 6, { align: 'center' });
  y += 11;

  // 2) Infos sinistre
  doc.setFontSize(PDF_FS_SECTION); doc.setFont('helvetica', 'bold'); doc.setTextColor(...GB);
  doc.text(S(d('offre.pdf.sections.sinistre')), ML, y); y += PDF_GAP_TITRE;
  autoTable(doc, {
    startY: y,
    body: [
      [S(d('offre.pdf.champs.numeroSinistre')), S(sinistre?.numeroSinistreLocal || '-'), S(d('offre.pdf.champs.dateDocument')), S(now)],
      [S(d('offre.pdf.champs.dateAccident')), S(fmtDate(sinistre?.dateAccident)), S(d('offre.pdf.champs.dateDeclaration')), S(fmtDate(sinistre?.dateDeclaration))],
    ],
    margin: { left: ML, right: 15, top: PDF_HEADER_H + 2, bottom: PDF_FOOTER_H + 4 },
    styles: { fontSize: PDF_FS_TABLE, cellPadding: PDF_CELL_PAD },
    columnStyles: {
      0: { fontStyle: 'bold', fillColor: [248, 250, 252], cellWidth: infoColW },
      2: { fontStyle: 'bold', fillColor: [248, 250, 252], cellWidth: infoColW },
    },
    tableLineColor: [229, 231, 235], tableLineWidth: 0.2,
  });
  y = doc.lastAutoTable.finalY + PDF_GAP_SECTION;

  // 3) Infos victime
  doc.setFontSize(PDF_FS_SECTION); doc.setFont('helvetica', 'bold'); doc.setTextColor(...GB);
  doc.text(S(d('offre.pdf.sections.victime')), ML, y); y += PDF_GAP_TITRE;
  autoTable(doc, {
    startY: y,
    body: [
      [S(d('offre.pdf.champs.nomPrenoms')), S(`${victime.nom} ${victime.prenoms}`), S(d('offre.pdf.champs.type')), S(d(estDecede ? 'offre.pdf.champs.decede' : 'offre.pdf.champs.blesse'))],
      [S(d('offre.pdf.champs.dateNaissance')), S(fmtDate(victime.dateNaissance)), S(d('offre.pdf.champs.tauxRc')), S(`${offre?.tauxPartageRc ?? 100} %`)],
    ],
    margin: { left: ML, right: 15, top: PDF_HEADER_H + 2, bottom: PDF_FOOTER_H + 4 },
    styles: { fontSize: PDF_FS_TABLE, cellPadding: PDF_CELL_PAD },
    columnStyles: {
      0: { fontStyle: 'bold', fillColor: [248, 250, 252], cellWidth: infoColW },
      2: { fontStyle: 'bold', fillColor: [248, 250, 252], cellWidth: infoColW },
    },
    tableLineColor: [229, 231, 235], tableLineWidth: 0.2,
  });
  y = doc.lastAutoTable.finalY + PDF_GAP_SECTION;

  // 4) Expertise medicale (blesse seulement)
  if (!estDecede && expertise) {
    doc.setFontSize(PDF_FS_SECTION); doc.setFont('helvetica', 'bold'); doc.setTextColor(...GB);
    doc.text(S(d('offre.pdf.sections.expertise')), ML, y); y += PDF_GAP_TITRE;
    // Echelle des souffrances : code du referentiel, libelle dans la langue du document.
    const echelle = (code) => d(`offre.pdf.echelle.${code || 'NEANT'}`, { defaultValue: (code || 'NEANT').replace(/_/g, ' ') });
    autoTable(doc, {
      startY: y,
      body: [
        [S(d('offre.pdf.champs.ipp')), S(`${expertise.tauxIpp ?? 0} %`), S(d('offre.pdf.champs.itt')), S(d('offre.pdf.champs.ittJours', { jours: expertise.dureeIttJours ?? 0 }))],
        [S(d('offre.pdf.champs.pretiumDoloris')), S(echelle(expertise.pretiumDoloris)), S(d('offre.pdf.champs.prejudiceEsthetique')), S(echelle(expertise.prejudiceEsthetique))],
        [S(d('offre.pdf.champs.tiercePersonne')), S(d(expertise.necessiteTiercePersonne ? 'offre.pdf.champs.oui' : 'offre.pdf.champs.non')), S(''), S('')],
      ],
      margin: { left: ML, right: 15 },
      styles: { fontSize: PDF_FS_TABLE, cellPadding: PDF_CELL_PAD },
      columnStyles: {
        0: { fontStyle: 'bold', fillColor: [248, 250, 252], cellWidth: infoColW },
        2: { fontStyle: 'bold', fillColor: [248, 250, 252], cellWidth: infoColW },
      },
      tableLineColor: [229, 231, 235], tableLineWidth: 0.2,
    });
    y = doc.lastAutoTable.finalY + PDF_GAP_SECTION;
  }

  // 5) Chefs de prejudice
  doc.setFontSize(PDF_FS_SECTION); doc.setFont('helvetica', 'bold'); doc.setTextColor(...GB);
  doc.text(S(d('offre.pdf.sections.chefs')), ML, y); y += PDF_GAP_TITRE;

  const chefs = (estDecede ? [
    { label: d('offre.pdf.chefs.fraisFuneraires'), val: offre?.montantFraisFuneraires, art: 'Art.264' },
    { label: d('offre.pdf.chefs.prejEconomique'), val: offre?.montantPrejEconomique, art: 'Art.265' },
    { label: d('offre.pdf.chefs.prejMoral'), val: offre?.montantPrejMoral, art: 'Art.266' },
  ] : [
    { label: d('offre.pdf.chefs.fraisMedicaux'), val: offre?.montantFraisMedicaux, art: 'Art.258' },
    { label: d('offre.pdf.chefs.itt'), val: offre?.montantItt, art: 'Art.259' },
    { label: d('offre.pdf.chefs.prejPhysiologique'), val: offre?.montantPrejPhysiologique, art: 'Art.260-a' },
    { label: d('offre.pdf.chefs.prejEconomique'), val: offre?.montantPrejEconomique, art: 'Art.260-b' },
    { label: d('offre.pdf.chefs.prejMoral'), val: offre?.montantPrejMoral, art: 'Art.260-c' },
    { label: d('offre.pdf.chefs.tiercePersonne'), val: offre?.montantTiercePersonne, art: 'Art.261' },
    { label: d('offre.pdf.chefs.pretiumDoloris'), val: offre?.montantPretiumDoloris, art: 'Art.262' },
    { label: d('offre.pdf.chefs.prejEsthetique'), val: offre?.montantPrejEsthetique, art: 'Art.262' },
    { label: d('offre.pdf.chefs.prejCarriere'), val: offre?.montantPrejCarriere, art: 'Art.263' },
    { label: d('offre.pdf.chefs.prejScolaire'), val: offre?.montantPrejScolaire, art: 'Art.263-1' },
    { label: d('offre.pdf.chefs.prejLeses'), val: offre?.montantPrejLeses, art: 'Art.229' },
  ]).filter(c => (c.val || 0) > 0);

  autoTable(doc, {
    startY: y,
    head: [[S(d('offre.pdf.tableau.chef')), S(d('offre.pdf.tableau.montant'))]],
    body: chefs.length > 0
      ? chefs.map(c => [S(`${c.art}  ${c.label}`), S(fmt(c.val))])
      : [[S(d('offre.pdf.tableau.aucunChef')), '']],
    margin: { left: ML, right: 15, top: PDF_HEADER_H + 2, bottom: PDF_FOOTER_H + 4 },
    styles: { fontSize: PDF_FS_TABLE, cellPadding: 2 },
    headStyles: { fillColor: GB, textColor: [255, 255, 255], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: { 1: { halign: 'right' } },
    tableLineColor: [229, 231, 235], tableLineWidth: 0.2,
  });
  y = doc.lastAutoTable.finalY + 1;

  // Sous-totaux
  const deductRC = (offre?.totalBrut || 0) - (offre?.totalNet || 0);
  const summaryRows = [
    [S(d('offre.pdf.totaux.brut')), S(fmt(offre?.totalBrut))],
    // Le taux de l'offre est la part de l'assuré APPLIQUÉE ; la déduction en est le complément.
    [S(d('offre.pdf.totaux.partNonPrise', { taux: 100 - Number(offre?.tauxPartageRc ?? 100) })), S(`- ${fmt(deductRC)}`)],
    [S(d('offre.pdf.totaux.net')), S(fmt(offre?.totalNet))],
  ];
  if ((offre?.fraisGestion || 0) > 0) {
    summaryRows.push([S(d('offre.pdf.totaux.fraisGestion')), S(fmt(offre.fraisGestion))]);
  }
  autoTable(doc, {
    startY: y,
    body: summaryRows,
    margin: { left: ML, right: 15, top: PDF_HEADER_H + 2, bottom: PDF_FOOTER_H + 4 },
    styles: { fontSize: PDF_FS_TABLE, cellPadding: PDF_CELL_PAD },
    bodyStyles: { fillColor: [248, 250, 252], textColor: [80, 80, 80] },
    columnStyles: { 1: { halign: 'right' } },
    tableLineColor: [229, 231, 235], tableLineWidth: 0.2,
  });
  y = doc.lastAutoTable.finalY + 1;

  // Total en surbrillance
  autoTable(doc, {
    startY: y,
    body: [[S(d('offre.pdf.totaux.montantTotal')), S(fmt(offre?.montantTotalOffre))]],
    margin: { left: ML, right: 15, top: PDF_HEADER_H + 2, bottom: PDF_FOOTER_H + 4 },
    styles: { fontSize: 11, fontStyle: 'bold', cellPadding: 2.5 },
    bodyStyles: { fillColor: [240, 253, 244], textColor: GB },
    columnStyles: { 1: { halign: 'right' } },
    tableLineColor: GB, tableLineWidth: 0.4,
  });
  y = doc.lastAutoTable.finalY + PDF_GAP_SECTION;

  // SMIG
  doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(130, 130, 130);
  doc.text(S(d('offre.pdf.totaux.smig', { montant: fmt(offre?.smigMensuelRetenu) })), ML, y);
  y += 5;

  // 6) Ayants droit (deces)
  if (estDecede && ayantsDroit.length > 0) {
    doc.setFontSize(PDF_FS_SECTION); doc.setFont('helvetica', 'bold'); doc.setTextColor(...GB);
    doc.text(S(d('offre.pdf.sections.ayantsDroit')), ML, y); y += PDF_GAP_TITRE;
    autoTable(doc, {
      startY: y,
      head: [[S(d('offre.pdf.ayantsDroit.nomPrenom')), S(d('offre.pdf.ayantsDroit.lien')), S(d('offre.pdf.ayantsDroit.pe')), S(d('offre.pdf.ayantsDroit.pm')), S(d('offre.pdf.ayantsDroit.total'))]],
      body: ayantsDroit.map(a => [
        S(`${a.nom} ${a.prenoms}`),
        // Lien inconnu : son code, comme avant ; absent : rien (une clé vide afficherait son chemin).
        S(a.lien ? d(`offre.pdf.liens.${a.lien}`, { defaultValue: a.lien }) : a.lien),
        S(fmt(a.montantPe)),
        S(fmt(a.montantPm)),
        S(fmt(a.montantTotal)),
      ]),
      margin: { left: ML, right: 15 },
      styles: { fontSize: PDF_FS_TABLE, cellPadding: PDF_CELL_PAD },
      headStyles: { fillColor: GB, textColor: [255, 255, 255] },
      columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right', fontStyle: 'bold' } },
      tableLineColor: [229, 231, 235], tableLineWidth: 0.2,
    });
    y = doc.lastAutoTable.finalY + 5;
  }

  // 7) Mention legale
  const versCompagnie = partieGarantie(sinistre).role === 'COMPAGNIE';
  const mention = destinataire === 'homologue'
    ? S(versCompagnie
      ? d('offre.pdf.mention.compagnie')
      : d('offre.pdf.mention.homologue'))
    : S(d('offre.pdf.mention.victime'));
  doc.setFontSize(9.5);
  const mentionLines = doc.splitTextToSize(mention, CW - 8);
  const mentionH = mentionLines.length * 4.6 + 6;
  /* Reserve de saut de page : si malgre le resserrage le contenu deborde
     (deces avec beaucoup d'ayants droit), mention et signatures partent ensemble. */
  const SIGNATURE_H = 33;
  if (y + mentionH + SIGNATURE_H > doc.internal.pageSize.getHeight() - 15) { doc.addPage(); y = 15; }
  doc.setFillColor(255, 251, 235); doc.setDrawColor(252, 211, 77);
  doc.rect(ML, y, CW, mentionH, 'FD');
  doc.setTextColor(120, 70, 0); doc.setFontSize(9.5); doc.setFont('helvetica', 'italic');
  doc.text(mentionLines, ML + 4, y + 5.5);
  y += mentionH + 9;

  // 8) Signatures - 20 mm de blanc sous chaque intitule, de quoi signer et cacheter.
  // L'ensemble est resserre pour que l'offre d'un blesse tienne sur UNE page
  // (recette du 2026-09-18 : mention et signatures partaient seules en page 2).
  const sigW = (CW - 10) / 2;
  const sigX2 = ML + sigW + 10;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(60, 60, 60);
  doc.text(S(d('offre.pdf.signatures.bncb')), ML, y);
  doc.text(S(d(destinataire === 'homologue' ? (versCompagnie ? 'offre.pdf.signatures.compagnie' : 'offre.pdf.signatures.homologue') : 'offre.pdf.signatures.victime')), sigX2, y);
  doc.setFont('helvetica', 'italic'); doc.setFontSize(8); doc.setTextColor(150, 150, 150);
  doc.text(S(d('offre.pdf.signatures.dateSignatureCachet')), ML, y + 5);
  doc.text(S(d(destinataire === 'homologue' ? 'offre.pdf.signatures.dateSignatureCachet' : 'offre.pdf.signatures.luEtApprouve')), sigX2, y + 5);
  /* La signature se loge dans les 26 mm deja blancs sous l'intitule, au-dessus du
     trait : elle remplace le vide prevu pour signer a la main, sans rien allonger.
     Seule la colonne du bureau la porte - l'autre est celle du destinataire, qui
     signe de sa main. */
  if (signature) {
    try {
      doc.addImage(signature, 'PNG', ML, y + 9, 45, 20, undefined, 'FAST');
    } catch {
      // Image illisible : le document sort avec son blanc, comme avant. Ne jamais
      // empecher une impression pour une signature.
    }
  }
  doc.setDrawColor(0, 0, 0);
  doc.line(ML, y + 25, ML + sigW, y + 25);
  doc.line(sigX2, y + 25, MR, y + 25);

  // Stamp header + footer on every page
  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    drawPdfHeader(doc, ML, MR);
    drawPdfFooter(doc, ML, MR, p, totalPages);
  }

  return doc.output('blob');
}

/**
 * La signature du responsable, en data URL, ou null.
 *
 * POURQUOI UNE DATA URL ET NON UNE BALISE IMG. Ce document n'est pas du HTML : il est
 * assemblé par jsPDF, qui veut les octets de l'image, pas une adresse. Il faut donc
 * l'avoir AVANT de composer la page - d'où l'impression devenue asynchrone.
 *
 * Un refus du serveur rend null et le document sort sans signature, comme avant :
 * l'immense majorité des offres n'est pas signée, et une erreur ici empêcherait
 * d'imprimer ce qui s'imprimait très bien.
 */
async function chargerSignatureOffre(sinistre) {
  try {
    const r = await axiosInstance.get('/v1/signatures/document/OFFRE_INDEMNISATION', {
      params: sinistre?.sinistreTrackingId ? { sinistre: sinistre.sinistreTrackingId } : undefined,
      responseType: 'blob',
      skipErrorToast: true,
    });
    return await new Promise((resolve) => {
      const lecteur = new FileReader();
      lecteur.onloadend = () => resolve(lecteur.result);
      lecteur.onerror = () => resolve(null);
      lecteur.readAsDataURL(r.data);
    });
  } catch {
    return null;
  }
}

/** Langue de l'offre : celle de la victime, ou celle du partenaire à qui elle part
    (bureau homologue pour un ET, compagnie togolaise pour un TE). */
const langueOffre = (victime, sinistre, destinataire) => (destinataire === 'homologue'
  ? langueDocument(partieGarantie(sinistre).organismeTrackingId)
  : Promise.resolve(langueVictime(victime)));

/** Imprime EXACTEMENT le document envoyé au destinataire (victime ou homologue). */
export async function ouvrirImpressionOffre(victime, sinistre, offre, destinataire = 'homologue', expertise = null, ayantsDroit = []) {
  const [signature, langue] = await Promise.all([
    chargerSignatureOffre(sinistre),
    langueOffre(victime, sinistre, destinataire),
  ]);
  // Boîte d'impression directe : l'onglet ouvert affichait le PDF en pleine page.
  imprimerPdf(genererPdfOffre(victime, sinistre, offre, destinataire, expertise, ayantsDroit, signature, langue));
}


/* ─────────────────────────────────────────────────────────────────────────────
   COMPOSANT PRINCIPAL - WorkflowOffre
───────────────────────────────────────────────────────────────────────────── */
/* Ordre des statuts de l'offre au paiement, pour ne jamais faire reculer un dossier que
   une autre victime a déjà porté plus loin. Même échelle que le backend (DelaiServiceImpl). */
const RANG_STATUT = {
  ATTENTE_PIECES_DE_RECLAMATION: 30, MUR: 40, OFFRE_EFFECTUE: 50, ATTENTE_OFFRE: 52,
  OFFRE_RECUE: 55, ACCORD: 60, REJET: 60, BAP: 70, ATTENTE_CHEQUE: 75, CHEQUE_RECU: 80,
  PARTIELLEMENT_PAYE: 85, SOLDE: 90, CLOTURE: 100,
};

export default function WorkflowOffre({ offre, victime, sinistre, onRefresh, expertise = null, ayantsDroit = [], onTransformerEnReglement }) {
  const { t } = useTranslation('dossierFinance');
  const te = sinistre?.typeSinistre === 'SURVENU_ETRANGER';
  const phase = getPhaseOffre(offre, te);
  const homologue = sinistre?.organismeHomologueRaisonSociale || '';
  const offreDeLHomologue = offre?.origineOffre === 'HOMOLOGUE';
  /* L'offre part au partenaire du dossier : bureau homologue pour un ET, compagnie
     togolaise pour un TE - et sa réponse en revient. */
  const partie = partieGarantie(sinistre);
  const parCompagnie = partie.role === 'COMPAGNIE';
  const [emailPartie, setEmailPartie] = useState('');
  useEffect(() => {
    let actif = true;
    adresseGarantie(sinistre).then((e) => { if (actif) setEmailPartie(e); });
    return () => { actif = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sinistre?.sinistreTrackingId, sinistre?.typeSinistre]);

  /* Langue de ce qui part : celle du partenaire pour l'offre qu'on lui soumet, celle
     de la victime pour l'offre qu'on lui présente. Jamais celle de l'écran. */
  const { langue: langueHomologue, pret: langueHomologuePrete } = useLangueDocument(partie.organismeTrackingId);
  const langueVict = langueVictime(victime);

  const [mailHomologueOpen, setMailHomologueOpen] = useState(false);
  const [contreOffreOpen, setContreOffreOpen] = useState(false);
  const [mailVictimeOpen, setMailVictimeOpen] = useState(false);
  const [accordOpen, setAccordOpen] = useState(false);
  const [etapeTe, setEtapeTe] = useState(null);
  const [saving, setSaving] = useState(false);

  const [fichierContreOffre, setFichierContreOffre] = useState(null);
  const [fichierAccord, setFichierAccord] = useState(null);
  const [mailHomologueRelance, setMailHomologueRelance] = useState(false);
  const [mailVictimeRelance, setMailVictimeRelance] = useState(false);

  const [formContreOffre] = Form.useForm();
  const [formAccord] = Form.useForm();
  const [formEtapeTe] = Form.useForm();
  const [fichierEtapeTe, setFichierEtapeTe] = useState(null);

  // Ne rien afficher si l'offre n'est pas encore validée en interne
  if (!offre?.dateValidation) return null;

  /* ── Helpers email ─────────────────────────────────────────────────────── */
  const emailHomologue = emailPartie;
  const emailVictime = victime?.email || victime?.mail || '';
  const montantTransmis = offre?.montantContreOffre ?? offre?.montantTotalOffre;
  const aContreOffre = offre?.montantContreOffre != null
    && offre.montantContreOffre !== offre.montantTotalOffre;

  const nomVictime = `${victime.nom} ${victime.prenoms}`;

  const genMailContent = (dest, isRelance = false) => contenuMailOffre({
    dest, isRelance, offre, sinistre, victime,
    langue: dest === 'homologue' ? langueHomologue : langueVict,
  });

  /* ── Helper : PATCH générique sur l'offre ─────────────────────────────── */
  const patchOffre = (champs) =>
    axiosInstance.patch(`/v1/indemnisation/offre/${offre.offreTrackingId}`, champs);

  /**
   * Fait avancer le dossier le long de la machine d'état, une étape après l'autre.
   *
   * Ces appels étaient tous en `.catch(() => {})`. L'échec était donc invisible
   * ICI - mais l'intercepteur global d'axios affichait quand même le message du
   * backend, ce qui produisait un toast d'erreur sans rapport apparent avec
   * l'action en cours pendant que le workflow, lui, annonçait un succès. D'où le
   * « ça a passé mais rien n'a changé ».
   *
   * On passe plusieurs cibles quand le chemin légal compte plusieurs sauts
   * (OFFRE_RECUE → ACCORD → BAP). Un échec intermédiaire n'est pas signalé : ce
   * qui compte est d'atteindre la dernière cible, et le backend refuse par
   * ailleurs les statuts déjà atteints.
   */
  const avancerDossier = async (...cibles) => {
    let echec = null;
    let courant = sinistre?.statut;
    for (const cible of cibles) {
      // Chaque victime avance à son rythme (décision du 2026-09-15) : le dossier suit la
      // plus avancée. Une étape qu'une autre victime a déjà fait franchir au dossier est
      // enregistrée sur l'offre, sans faire reculer le dossier ni afficher d'échec.
      if (RANG_STATUT[cible] !== undefined && RANG_STATUT[courant] !== undefined
        && RANG_STATUT[cible] <= RANG_STATUT[courant]) {
        echec = null;
        continue;
      }
      try {
        await axiosInstance.patch(
          `/v1/sinistres/${sinistre.sinistreTrackingId}/statut`, null,
          { params: { statut: cible }, skipErrorToast: true },
        );
        courant = cible;
        echec = null;
      } catch (e) {
        echec = e?.response?.data?.message || t('workflow.statut.passageRefuse', { cible });
      }
    }
    if (echec) {
      toast.warning(
        t('workflow.statut.inchange', { echec }),
        { autoClose: 9000 },
      );
      return false;
    }
    return true;
  };

  /* ── STEP 1 : envoi homologue ──────────────────────────────────────────── */
  const marquerEnvoyeHomologue = async () => {
    setSaving(true);
    try {
      await patchOffre({ dateEnvoiHomologue: dayjs().format('YYYY-MM-DD') });
      await avancerDossier('ATTENTE_OFFRE');
      toast.success(parCompagnie ? t('workflow.toasts.envoyeeCompagnie') : t('workflow.toasts.envoyeeHomologue'));
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('workflow.erreur'));
    } finally { setSaving(false); }
  };

  const onMailHomologueSent = async () => {
    setMailHomologueOpen(false);
    if (mailHomologueRelance) { toast.success(parCompagnie ? t('workflow.toasts.relanceCompagnie') : t('workflow.toasts.relanceHomologue')); }
    else { await marquerEnvoyeHomologue(); }
  };

  const onTelechargerHomologue = () => {
    ouvrirImpressionOffre(victime, sinistre, offre, 'homologue', expertise, ayantsDroit);
    Modal.confirm({
      title: t('workflow.envoiPostal.titre'),
      content: t('workflow.envoiPostal.texte'),
      okText: t('workflow.envoiPostal.oui'),
      cancelText: t('workflow.envoiPostal.non'),
      okButtonProps: { style: { background: G, borderColor: G } },
      onOk: marquerEnvoyeHomologue,
    });
  };

  /* ── STEP 2 : réponse homologue ────────────────────────────────────────── */
  const onOffreValideeHomologue = async () => {
    setSaving(true);
    try {
      await patchOffre({
        dateReponseHomologue: dayjs().format('YYYY-MM-DD'),
        montantContreOffre: null,
      });
      await avancerDossier('OFFRE_RECUE');
      toast.success(parCompagnie ? t('workflow.toasts.valideeCompagnie') : t('workflow.toasts.valideeHomologue'));
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('workflow.erreur'));
    } finally { setSaving(false); }
  };

  const onContreOffreSubmit = async (vals) => {
    setSaving(true);
    try {
      const gedId = await uploadGed(
        fichierContreOffre,
        // Titre GED : donnee partagee, en francais quelle que soit la langue de l'ecran.
        `Contre-offre ${partie.role === 'COMPAGNIE' ? 'compagnie' : 'bureau homologue'} - ${sinistre?.numeroSinistreLocal}`,
        'OFFRE',
        sinistre,
        victime.victimeTrackingId,
      );
      await patchOffre({
        dateReponseHomologue: vals.dateReponse?.format('YYYY-MM-DD') || dayjs().format('YYYY-MM-DD'),
        montantContreOffre: vals.montantContreOffre,
        descriptionContreOffre: vals.description || null,
        ossanGedDocumentIdContreOffre: gedId,
      });
      await avancerDossier('OFFRE_RECUE');
      setContreOffreOpen(false);
      formContreOffre.resetFields();
      setFichierContreOffre(null);
      toast.success(t('workflow.toasts.contreOffreEnregistree'));
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('workflow.toasts.erreurContreOffre'));
    } finally { setSaving(false); }
  };

  /* ── STEP 3 : envoi victime ────────────────────────────────────────────── */
  const marquerEnvoyeVictime = async () => {
    setSaving(true);
    try {
      await patchOffre({ dateEnvoiVictime: dayjs().format('YYYY-MM-DD') });
      toast.success(t('workflow.toasts.transmiseVictime'));
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('workflow.erreur'));
    } finally { setSaving(false); }
  };

  const onMailVictimeSent = async () => {
    setMailVictimeOpen(false);
    if (mailVictimeRelance) { toast.success(t('workflow.toasts.relanceVictime')); }
    else { await marquerEnvoyeVictime(); }
  };

  const onTelechargerVictime = () => {
    ouvrirImpressionOffre(victime, sinistre, offre, 'victime', expertise, ayantsDroit);
    Modal.confirm({
      title: t('workflow.remiseVictime.titre'),
      content: t('workflow.remiseVictime.texte'),
      okText: t('workflow.remiseVictime.oui'),
      cancelText: t('workflow.remiseVictime.non'),
      okButtonProps: { style: { background: G, borderColor: G } },
      onOk: marquerEnvoyeVictime,
    });
  };

  /* ── STEP 4 : accord / rejet victime ───────────────────────────────────── */
  const onAccordSubmit = async (vals) => {
    setSaving(true);
    try {
      const gedId = await uploadGed(
        fichierAccord,
        `Accord victime - ${sinistre?.numeroSinistreLocal}`,
        /* « ACCORD » et non « ACCORD_VICTIME ». Deux nomenclatures voisines se
           ressemblent ici : ACCORD_VICTIME est une NATURE DE COURRIER, employee
           par la messagerie et les bordereaux ; le type de document GED, lui,
           s'appelle ACCORD et porte deja le libelle « Accord victime »
           (constants.js, TYPES_DOC_GED). Envoyer la premiere a une route qui
           attend la seconde faisait echouer le depot sur un
           « Cannot deserialize value of type TypeDocumentOssanGed ». */
        'ACCORD',
        sinistre,
        victime.victimeTrackingId,
      );
      await patchOffre({
        dateAccordVictime: vals.dateAccord?.format('YYYY-MM-DD') || dayjs().format('YYYY-MM-DD'),
        observationsAccord: vals.observations || null,
        ossanGedDocumentIdAccord: gedId,
      });

      // Passer le sous-dossier victime en BAP
      if (victime.dossierTrackingId) {
        try {
          await axiosInstance.patch(
            `/v1/dossiers-reclamation/${victime.dossierTrackingId}/statut`,
            null, { params: { statut: 'BAP' }, skipErrorToast: true },
          );
        } catch (e) {
          toast.warning(
            t('workflow.accord.pasBap', { motif: e?.response?.data?.message || t('workflow.accord.changementRefuse') }),
            { autoClose: 9000 },
          );
        }
      }

      setAccordOpen(false);
      formAccord.resetFields();
      setFichierAccord(null);

      /* Décision du 2026-09-15 : une victime d'accord passe au bon à payer sans attendre
         les autres. Le chemin légal reste OFFRE_RECUE → ACCORD → BAP ; un dossier déjà
         porté plus loin par une autre victime ne recule pas (voir avancerDossier). */
      if (te) {
        await avancerDossier('ACCORD', 'BAP', 'ATTENTE_CHEQUE');
        toast.success(t('workflow.accord.enregistreTe', { compagnie: partie.libelle || '' }).trim());
      } else {
        await avancerDossier('ACCORD', 'BAP');
        toast.success(t('workflow.accord.enregistre'));
      }
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('workflow.accord.erreur'));
    } finally { setSaving(false); }
  };

  const onRejet = async () => {
    setSaving(true);
    try {
      await patchOffre({ dateRejetVictime: dayjs().format('YYYY-MM-DD') });
      /* Le refus d'une victime ne met le dossier en Rejet que s'il ne reste personne
         d'autre à indemniser : sinon il bloquerait les victimes qui, elles, avancent
         (depuis Rejet, le dossier ne peut plus passer à l'accord). */
      let autresEnCours = true;
      try {
        const [resOffres, resVictimes] = await Promise.all([
          axiosInstance.get(`/v1/indemnisation/offres/sinistre/${sinistre.sinistreTrackingId}`),
          axiosInstance.get(`/v1/victimes/sinistre/${sinistre.sinistreTrackingId}`),
        ]);
        const offres = resOffres.data?.data || [];
        const nbPersonnes = (resVictimes.data?.data || []).filter(estVictimeIndemnisable).length;
        const autresRejetees = offres.filter(o => o.offreTrackingId !== offre.offreTrackingId && o.dateRejetVictime).length;
        autresEnCours = nbPersonnes - 1 - autresRejetees > 0;
      } catch { /* liste indisponible : prudence, le dossier ne bouge pas */ }
      if (autresEnCours) {
        toast.info(t('workflow.rejet.pourCetteVictime'));
        onRefresh?.();
        return;
      }
      await avancerDossier('REJET');
      toast.info(t('workflow.rejet.dossierRejete'));
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('workflow.erreur'));
    } finally { setSaving(false); }
  };

  /* ── Circuit TE : chèque de la compagnie → homologue → quittance ────────── */
  const ETAPES_TE = {
    REPONSE_HOMOLOGUE: {
      titre: t('workflow.te.reponseHomologue.titre', { homologue }).trim(),
      ok: t('workflow.te.enregistrerTransmission'), date: t('workflow.te.reponseHomologue.date'),
      /* La transmission à l'homologue VAUT transmission de l'offre : c'est lui qui
         la remet à la victime là-bas et recueille sa réponse. Une seule saisie,
         le workflow avance à « Accord ». */
      champs: (v) => ({
        dateReponseTransmiseHomologue: v.date.format('YYYY-MM-DD'),
        dateEnvoiVictime: v.date.format('YYYY-MM-DD'),
      }),
      succes: t('workflow.te.reponseHomologue.succes'),
    },
    CHEQUE: {
      titre: t('workflow.te.cheque.titre', { compagnie: partie.libelle || '' }).trim(),
      // Titre du document depose en GED : donnee partagee, en francais.
      titreGed: `Chèque reçu de la compagnie ${partie.libelle || ''}`.trim(),
      ok: t('workflow.te.cheque.ok'), date: t('workflow.te.dateReception'), cheque: true, doc: t('workflow.te.cheque.doc'),
      champs: (v, gedId) => ({
        dateChequeCompagnie: v.date.format('YYYY-MM-DD'),
        montantChequeCompagnie: v.montant ?? null,
        referenceChequeCompagnie: v.reference || null,
        ossanGedDocumentIdCheque: gedId,
      }),
      avancer: ['BAP', 'ATTENTE_CHEQUE', 'CHEQUE_RECU'],
      succes: t('workflow.te.cheque.succes'),
    },
    TRANSMISSION: {
      titre: t('workflow.te.transmission.titre', { homologue }).trim(),
      ok: t('workflow.te.enregistrerTransmission'), date: t('workflow.te.transmission.date'),
      champs: (v) => ({ dateTransmissionCheque: v.date.format('YYYY-MM-DD') }),
      succes: t('workflow.te.transmission.succes'),
    },
    QUITTANCE: {
      titre: t('workflow.te.quittance.titre', { homologue }).trim(),
      titreGed: `Quittance reçue du bureau homologue ${homologue}`.trim(),
      ok: t('workflow.te.quittance.ok'), date: t('workflow.te.dateReception'), doc: t('workflow.te.quittance.doc'),
      champs: (v, gedId) => ({
        dateQuittanceHomologue: v.date.format('YYYY-MM-DD'),
        ossanGedDocumentIdQuittance: gedId,
      }),
      succes: t('workflow.te.quittance.succes'),
    },
  };

  const onEtapeTeSubmit = async (vals) => {
    const etape = ETAPES_TE[etapeTe];
    if (!etape) return;
    setSaving(true);
    try {
      const gedId = etape.doc
        ? await uploadGed(fichierEtapeTe, `${etape.titreGed} - ${sinistre?.numeroSinistreLocal} - ${nomVictime}`,
          'AUTRES', sinistre, victime.victimeTrackingId)
        : null;
      await patchOffre(etape.champs(vals, gedId));
      if (etape.avancer) await avancerDossier(...etape.avancer);
      if (etapeTe === 'QUITTANCE') {
        // Soldé quand toutes les personnes à indemniser ont leur quittance ; sinon partiellement payé.
        let toutes = false;
        try {
          const [resOffres, resVictimes] = await Promise.all([
            axiosInstance.get(`/v1/indemnisation/offres/sinistre/${sinistre.sinistreTrackingId}`),
            axiosInstance.get(`/v1/victimes/sinistre/${sinistre.sinistreTrackingId}`),
          ]);
          const offres = resOffres.data?.data || [];
          const nb = (resVictimes.data?.data || []).filter(estVictimeIndemnisable).length;
          toutes = nb > 0 && offres.filter(o => o.offreTrackingId === offre.offreTrackingId || o.dateQuittanceHomologue).length >= nb;
        } catch { /* prudence : partiellement payé */ }
        await avancerDossier('CHEQUE_RECU', toutes ? 'SOLDE' : 'PARTIELLEMENT_PAYE');
      }
      toast.success(etape.succes);
      setEtapeTe(null);
      formEtapeTe.resetFields();
      setFichierEtapeTe(null);
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('workflow.te.erreur'));
    } finally { setSaving(false); }
  };

  const blocTe = (fond, bord, couleur, icone, titre, detail, boutons) => (
    <div style={{ background: fond, borderRadius: 8, padding: '12px 16px', border: `1px solid ${bord}` }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: couleur, marginBottom: 6 }}>{icone}{titre}</div>
      {detail && <div style={{ fontSize: 11.5, color: '#6b7280', marginBottom: boutons ? 10 : 0 }}>{detail}</div>}
      {boutons && <Space wrap>{boutons}</Space>}
    </div>
  );
  const boutonTe = (cle, libelle) => (
    <Button icon={<CheckCircleOutlined />} type="primary" size="small"
      style={{ background: G, borderColor: G, borderRadius: 20 }}
      onClick={() => { formEtapeTe.resetFields(); setFichierEtapeTe(null); setEtapeTe(cle); }}>
      {libelle}
    </Button>
  );
  const fmtJour = (d) => (d ? dayjs(d).format('DD/MM/YYYY') : '-');

  /* ── Rendu de la phase courante ────────────────────────────────────────── */
  const renderAction = () => {
    if (phase === 6) return blocTe('#fffbeb', '#fcd34d', '#92400e', <SyncOutlined spin style={{ marginRight: 6 }} />,
      t('workflow.phase6.titre', { compagnie: partie.libelle || '' }).trim(),
      <Trans t={t} i18nKey="workflow.phase6.detail" values={{ date: fmtJour(offre.dateAccordVictime), montant: formatMontant(montantTransmis) }} components={{ b: <strong /> }} />,
      [boutonTe('CHEQUE', t('workflow.phase6.bouton'))]);

    if (phase === 7) return blocTe('#eff6ff', '#bfdbfe', '#1e40af', <SendOutlined style={{ marginRight: 6 }} />,
      t('workflow.phase7.titre', { homologue }).trim(),
      <>{t('workflow.phase7.recuLe', { date: fmtJour(offre.dateChequeCompagnie) })}
        {offre.montantChequeCompagnie != null && <> · <strong>{formatMontant(offre.montantChequeCompagnie)}</strong></>}
        {offre.referenceChequeCompagnie && <> · {t('workflow.phase7.numero', { numero: offre.referenceChequeCompagnie })}</>}</>,
      [boutonTe('TRANSMISSION', t('workflow.phase7.bouton'))]);

    if (phase === 8) return blocTe('#fdf4ff', '#e9d5ff', '#7c3aed', <SyncOutlined spin style={{ marginRight: 6 }} />,
      t('workflow.phase8.titre', { homologue }).trim(),
      offre.montantChequeCompagnie != null ? t('workflow.phase8.detailMontant', { date: fmtJour(offre.dateTransmissionCheque), montant: formatMontant(offre.montantChequeCompagnie) })
        : t('workflow.phase8.detail', { date: fmtJour(offre.dateTransmissionCheque) }),
      [boutonTe('QUITTANCE', t('workflow.phase8.bouton'))]);

    if (phase === 9) return blocTe('#f0fdf4', '#86efac', '#166534', <CheckCircleOutlined style={{ marginRight: 6 }} />,
      t('workflow.phase9.titre'),
      t('workflow.phase9.detail', { dateQuittance: fmtJour(offre.dateQuittanceHomologue), dateTransmission: fmtJour(offre.dateTransmissionCheque) }),
      null);

    if (phase === 5) return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 14px', background: '#f0fdf4',
        borderRadius: 8, border: '1px solid #86efac',
      }}>
        <CheckCircleOutlined style={{ color: '#16a34a', fontSize: 18 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: '#166534', fontSize: 13 }}>
            {t('workflow.phase5.titre')}
          </div>
          <div style={{ fontSize: 11, color: '#6b7280' }}>
            {offre.dateAccordVictime
              ? `${t('workflow.leDate', { date: dayjs(offre.dateAccordVictime).format('DD/MM/YYYY') })} `
              : ''}
            {formatMontant(montantTransmis)}
          </div>
        </div>
        {/* L'accord acquis, l'acte suivant est le règlement : on ouvre le formulaire déjà
            rempli du montant accordé et de la victime, plutôt que de le ressaisir. */}
        {onTransformerEnReglement && (
          <Button
            type="primary" size="small" icon={<WalletOutlined />}
            onClick={() => onTransformerEnReglement({
              victimeTrackingId: victime?.victimeTrackingId,
              montant: Number(montantTransmis) || 0,
              typePrejudice: victime?.typeVictime === 'DECEDE' || victime?.typeVictime === 'BLESSE' ? 'CORPOREL' : 'MATERIEL',
            })}
            style={{ background: '#166534', borderColor: '#166534' }}
          >
            {t('workflow.phase5.transformer')}
          </Button>
        )}
      </div>
    );

    if (phase === -1) return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 14px', background: '#fef2f2',
        borderRadius: 8, border: '1px solid #fca5a5',
      }}>
        <CloseCircleOutlined style={{ color: '#dc2626', fontSize: 18 }} />
        <div>
          <div style={{ fontWeight: 600, color: '#dc2626', fontSize: 13 }}>
            {t('workflow.rejet.titre')}
          </div>
          <div style={{ fontSize: 11, color: '#6b7280' }}>
            {offre.dateRejetVictime
              ? `${t('workflow.leDate', { date: dayjs(offre.dateRejetVictime).format('DD/MM/YYYY') })} `
              : ''}
            {t('workflow.rejet.oriente')}
          </div>
        </div>
      </div>
    );

    if (phase === 1) return (
      <div style={{
        background: '#fffbeb', borderRadius: 8,
        padding: '12px 16px', border: '1px solid #fcd34d',
      }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#92400e', marginBottom: 6 }}>
          <SendOutlined style={{ marginRight: 6 }} />
          {(parCompagnie ? t('workflow.phase1.titreCompagnie', { libelle: partie.libelle || '' }) : t('workflow.phase1.titreHomologue', { libelle: partie.libelle || '' })).trim()}
        </div>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
          {offreDeLHomologue
            ? <><Trans t={t} i18nKey="workflow.phase1.offreHomologue" values={{ homologue, montant: formatMontant(offre.montantTotalOffre) }} components={{ b: <strong style={{ color: G }} /> }} />
              {offre.dateReceptionHomologue && <> {t('workflow.phase1.recueLe', { date: fmtJour(offre.dateReceptionHomologue) })}</>}
              {offre.montantEvaluationBncb != null && <> {t('workflow.phase1.evaluation', { montant: formatMontant(offre.montantEvaluationBncb) })}</>}</> : <Trans t={t} i18nKey="workflow.phase1.offreCbconnect" values={{ montant: formatMontant(offre.montantTotalOffre) }} components={{ b: <strong style={{ color: G }} /> }} />}
          {te && <div style={{ marginTop: 2 }}>{t('workflow.phase1.ensuite')}</div>}
        </div>
        <Space wrap>
          <Button icon={<MailOutlined />} type="primary" size="small"
            style={{ background: '#2563eb', borderColor: '#2563eb', borderRadius: 20 }}
            onClick={() => { setMailHomologueRelance(false); setMailHomologueOpen(true); }}>
            {t('workflow.phase1.envoyerMail')}
          </Button>
          <Button icon={<DownloadOutlined />} size="small" style={{ borderRadius: 20 }}
            onClick={onTelechargerHomologue}>
            {t('workflow.phase1.telecharger')}
          </Button>
        </Space>
      </div>
    );

    if (phase === 2) return (
      <div style={{
        background: '#eff6ff', borderRadius: 8,
        padding: '12px 16px', border: '1px solid #bfdbfe',
      }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#1e40af', marginBottom: 6 }}>
          <SyncOutlined spin style={{ marginRight: 6 }} />
          {(parCompagnie ? t('workflow.phase2.attenteCompagnie', { libelle: partie.libelle || '' }) : t('workflow.phase2.attenteHomologue', { libelle: partie.libelle || '' })).trim()}
        </div>
        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10 }}>
          {t('workflow.phase2.envoyeLe', { date: offre.dateEnvoiHomologue
            ? dayjs(offre.dateEnvoiHomologue).format('DD/MM/YYYY')
            : '-' })}
        </div>
        <Space wrap>
          <Button icon={<CheckCircleOutlined />} type="primary" size="small"
            style={{ background: G, borderColor: G, borderRadius: 20 }}
            loading={saving}
            onClick={() => Modal.confirm({
              title: te ? t('workflow.phase2.accordCompagnieQ') : parCompagnie ? t('workflow.phase2.valideeCompagnieQ') : t('workflow.phase2.valideeHomologueQ'),
              content: t('workflow.phase2.accepteeTelleQuelle', { montant: formatMontant(offre.montantTotalOffre) }),
              okText: te ? t('workflow.phase2.ouiDaccord') : t('workflow.phase2.ouiValidee'),
              cancelText: t('workflow.annuler'),
              okButtonProps: { style: { background: G, borderColor: G } },
              onOk: onOffreValideeHomologue,
            })}>
            {te ? t('workflow.phase2.accordCompagnie') : t('workflow.phase2.offreValidee')}
          </Button>
          <Tooltip title={partie.role === 'COMPAGNIE' ? t('workflow.phase2.compagniePropose') : t('workflow.phase2.homologuePropose')}>
            <Button icon={<SwapOutlined />} danger size="small" style={{ borderRadius: 20 }}
              onClick={() => setContreOffreOpen(true)}>
              {te ? t('workflow.phase2.contrePropositionCompagnie') : t('workflow.phase2.contreOffreRecue')}
            </Button>
          </Tooltip>
          <Button icon={<MailOutlined />} size="small"
            style={{ borderRadius: 20, borderColor: '#2563eb', color: '#2563eb' }}
            onClick={() => { setMailHomologueRelance(true); setMailHomologueOpen(true); }}>
            {t('workflow.relancer')}
          </Button>
        </Space>
      </div>
    );

    if (phase === 3 && te) return (
      <div style={{
        background: '#fdf4ff', borderRadius: 8,
        padding: '12px 16px', border: '1px solid #e9d5ff',
      }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#7c3aed', marginBottom: 6 }}>
          <UserOutlined style={{ marginRight: 6 }} />
          {t('workflow.phase3.transmettreHomologue', { homologue })}
        </div>
        <div style={{ fontSize: 11.5, color: '#6b7280', marginBottom: 8 }}>
          {t('workflow.phase3.remiseParHomologue')}
        </div>
        {aContreOffre && (
          <Alert type="warning" showIcon style={{ borderRadius: 6, marginBottom: 8, padding: '4px 10px' }}
            message={
              <span style={{ fontSize: 11 }}>
                <Trans t={t} i18nKey={parCompagnie ? 'workflow.phase3.contreOffreCompagnie' : 'workflow.phase3.contreOffreHomologue'}
                  values={{ montant: formatMontant(offre.montantContreOffre) }} components={{ b: <strong /> }} />
              </span>
            }
          />
        )}
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
          <Trans t={t} i18nKey="workflow.phase3.montantATransmettre" values={{ montant: formatMontant(montantTransmis) }}
            components={{ b: <strong style={{ color: '#7c3aed' }} /> }} />
        </div>
        <Button icon={<CheckCircleOutlined />} type="primary" size="small"
          style={{ background: '#7c3aed', borderColor: '#7c3aed', borderRadius: 20 }}
          onClick={() => { formEtapeTe.resetFields(); setEtapeTe('REPONSE_HOMOLOGUE'); }}>
          {t('workflow.te.enregistrerTransmission')}
        </Button>
      </div>
    );

    if (phase === 3) return (
      <div style={{
        background: '#fdf4ff', borderRadius: 8,
        padding: '12px 16px', border: '1px solid #e9d5ff',
      }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#7c3aed', marginBottom: 6 }}>
          <UserOutlined style={{ marginRight: 6 }} />
          {t('workflow.phase3.transmettreVictime')}
        </div>
        {aContreOffre && (
          <Alert type="warning" showIcon style={{ borderRadius: 6, marginBottom: 8, padding: '4px 10px' }}
            message={
              <span style={{ fontSize: 11 }}>
                <Trans t={t} i18nKey={parCompagnie ? 'workflow.phase3.contreOffreCompagnie' : 'workflow.phase3.contreOffreHomologue'}
                  values={{ montant: formatMontant(offre.montantContreOffre) }} components={{ b: <strong /> }} />
                {offre.montantContreOffre < offre.montantTotalOffre
                  ? <Tag color="red" style={{ marginLeft: 6, borderRadius: 20, fontSize: 10 }}>
                    −{formatMontant(offre.montantTotalOffre - offre.montantContreOffre)} vs CBConnect
                  </Tag>
                  : null}
              </span>
            }
          />
        )}
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
          <Trans t={t} i18nKey="workflow.phase3.montantATransmettre" values={{ montant: formatMontant(montantTransmis) }}
            components={{ b: <strong style={{ color: '#7c3aed' }} /> }} />
        </div>
        {/* Beaucoup de victimes n'ont pas d'adresse courriel. La remise papier
            n'est donc pas un repli : c'est la voie principale quand l'adresse
            manque, et elle marque l'offre comme transmise exactement comme le
            courriel. On met alors ce bouton en avant plutôt que de laisser
            croire que l'envoi par mail est obligatoire. */}
        <Space wrap>
          {emailVictime ? (
            <>
              <Button icon={<MailOutlined />} type="primary" size="small"
                style={{ background: '#7c3aed', borderColor: '#7c3aed', borderRadius: 20 }}
                onClick={() => { setMailVictimeRelance(false); setMailVictimeOpen(true); }}>
                {t('workflow.phase3.envoyerCourriel')}
              </Button>
              <Button icon={<DownloadOutlined />} size="small" style={{ borderRadius: 20 }}
                onClick={onTelechargerVictime}>
                {t('workflow.phase3.imprimerRemettre')}
              </Button>
            </>
          ) : (
            <>
              <Button icon={<DownloadOutlined />} type="primary" size="small"
                style={{ background: '#7c3aed', borderColor: '#7c3aed', borderRadius: 20 }}
                onClick={onTelechargerVictime}>
                {t('workflow.phase3.imprimerRemettre')}
              </Button>
              <Tooltip title={t('workflow.phase3.sansAdresse')}>
                <Button icon={<MailOutlined />} size="small" style={{ borderRadius: 20 }}
                  onClick={() => { setMailVictimeRelance(false); setMailVictimeOpen(true); }}>
                  {t('workflow.phase3.envoyerCourriel')}
                </Button>
              </Tooltip>
            </>
          )}
        </Space>
        {!emailVictime && (
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 8 }}>
            {t('workflow.phase3.sansAdresseNote')}
          </div>
        )}
      </div>
    );

    if (phase === 4) return (
      <div style={{
        background: '#fff7ed', borderRadius: 8,
        padding: '12px 16px', border: '1px solid #fed7aa',
      }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#c2410c', marginBottom: 6 }}>
          <ExclamationCircleOutlined style={{ marginRight: 6 }} />
          {te ? t('workflow.phase4.attenteViaHomologue', { homologue }) : t('workflow.phase4.attenteVictime')}
        </div>
        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10 }}>
          <Trans t={t} i18nKey={te ? 'workflow.phase4.transmisHomologue' : 'workflow.phase4.transmis'}
            values={{ date: offre.dateEnvoiVictime
            ? dayjs(offre.dateEnvoiVictime).format('DD/MM/YYYY')
            : '-', montant: formatMontant(montantTransmis) }}
            components={{ b: <strong /> }} />
        </div>
        <Space wrap>
          <Button icon={<CheckCircleOutlined />} type="primary" size="small"
            style={{ background: G, borderColor: G, borderRadius: 20 }}
            onClick={() => setAccordOpen(true)}>
            {t('workflow.phase4.accordRecu')}
          </Button>
          <Button icon={<MailOutlined />} size="small"
            style={{ borderRadius: 20, borderColor: '#7c3aed', color: '#7c3aed' }}
            onClick={() => {
              if (te) { setMailHomologueRelance(true); setMailHomologueOpen(true); }
              else { setMailVictimeRelance(true); setMailVictimeOpen(true); }
            }}>
            {te ? t('workflow.phase4.relancerHomologue') : t('workflow.relancer')}
          </Button>
          <Button danger size="small" icon={<CloseCircleOutlined />} style={{ borderRadius: 20 }}
            onClick={() => Modal.confirm({
              title: t('workflow.phase4.rejetTitre'),
              content: t('workflow.phase4.rejetTexte'),
              okText: t('workflow.phase4.confirmerRejet'),
              cancelText: t('workflow.annuler'),
              okButtonProps: { danger: true },
              onOk: onRejet,
            })}>
            {t('workflow.phase4.desaccord')}
          </Button>
        </Space>
      </div>
    );

    return null;
  };

  /* ── Stepper compact ───────────────────────────────────────────────────── */
  const stepCurrent = phase === -1 ? 4 : te ? (phase <= 4 ? phase : Math.min(phase - 1, 7)) : Math.min(phase, 5);
  const stepStatus = phase === -1 ? 'error' : (te ? phase === 9 : phase === 5) ? 'finish' : 'process';
  const jour = (d) => (d ? dayjs(d).format('DD/MM') : null);

  return (
    <div style={{ marginTop: 16 }}>
      <Divider style={{ margin: '10px 0', fontSize: 11, color: '#9ca3af' }}>
        {t('workflow.frise.titre')}
      </Divider>

      <Steps
        current={stepCurrent}
        status={stepStatus}
        size="small"
        labelPlacement="vertical"
        style={{ marginBottom: 14 }}
        items={[
          { title: offreDeLHomologue ? t('workflow.frise.offreHomologue') : t('workflow.frise.validee'), description: offreDeLHomologue ? jour(offre.dateReceptionHomologue) : null },
          {
            title: partie.role === 'COMPAGNIE' ? t('workflow.frise.compagnie') : t('workflow.frise.bureauHomologue'),
            description: offre.dateEnvoiHomologue
              ? dayjs(offre.dateEnvoiHomologue).format('DD/MM')
              : null,
          },
          {
            title: te ? t('workflow.frise.reponseCompagnie') : t('workflow.frise.reponse'),
            description: offre.dateReponseHomologue
              ? dayjs(offre.dateReponseHomologue).format('DD/MM')
              : null,
          },
          {
            title: te ? t('workflow.frise.transmiseHomologue') : t('workflow.frise.victime'),
            description: offre.dateEnvoiVictime
              ? dayjs(offre.dateEnvoiVictime).format('DD/MM')
              : null,
          },
          {
            title: phase === -1 ? t('workflow.frise.rejet') : te ? t('workflow.frise.accordViaHomologue') : t('workflow.frise.accord'),
            description: (offre.dateAccordVictime || offre.dateRejetVictime)
              ? dayjs(offre.dateAccordVictime || offre.dateRejetVictime).format('DD/MM')
              : null,
          },
          ...(te ? [
            { title: t('workflow.frise.chequeCompagnie'), description: jour(offre.dateChequeCompagnie) },
            { title: t('workflow.frise.transmisHomologue'), description: jour(offre.dateTransmissionCheque) },
            { title: t('workflow.frise.quittance'), description: jour(offre.dateQuittanceHomologue) },
          ] : []),
        ]}
      />

      {renderAction()}

      {/* ═══ Modal mail homologue ═══ */}
      <ModalEnvoyerMail
        open={mailHomologueOpen}
        onClose={() => setMailHomologueOpen(false)}
        onSent={onMailHomologueSent}
        titre={mailHomologueRelance ? t(parCompagnie ? 'workflow.mail.titreRelanceCompagnie' : 'workflow.mail.titreRelanceHomologue', { nom: victime.nom, prenoms: victime.prenoms })
          : t(parCompagnie ? 'workflow.mail.titreCompagnie' : 'workflow.mail.titreHomologue', { nom: victime.nom, prenoms: victime.prenoms })}
        titreDocument={tDocument(langueHomologue)(
          `offre.lettre.titres.${mailHomologueRelance ? 'relance' : 'offre'}${partie.role === 'COMPAGNIE' ? 'Compagnie' : 'Homologue'}`,
          { victime: `${victime.nom} ${victime.prenoms}` },
        )}
        langue={langueHomologue}
        destinataireEmail={emailHomologue}
        sinistre={sinistre}
        nature={mailHomologueRelance ? 'RELANCE' : 'OFFRE_INDEMNISATION'}
        destinataireOrganismeTrackingId={partie.organismeTrackingId}
        reponseAttendue={!mailHomologueRelance}
        // Langue du bureau pas encore connue : envoyer maintenant partirait en français.
        loading={saving || !langueHomologuePrete}
        documentBlob={genererPdfOffre(victime, sinistre, offre, 'homologue', expertise, ayantsDroit, null, langueHomologue)}
        nomFichier={`Offre_${sinistre?.numeroSinistreLocal || 'indemnisation'}_${victime.nom}.pdf`}
        sujetInitial={genMailContent('homologue', mailHomologueRelance).sujet}
        corpsInitial={genMailContent('homologue', mailHomologueRelance).corps}
      />

      {/* ═══ Modal contre-offre ═══ */}
      <Modal
        open={contreOffreOpen}
        title={<Space><SwapOutlined style={{ color: '#f97316' }} />{parCompagnie ? t('workflow.contreOffre.titreCompagnie') : t('workflow.contreOffre.titreHomologue')}</Space>}
        onCancel={() => { setContreOffreOpen(false); formContreOffre.resetFields(); setFichierContreOffre(null); }}
        onOk={() => formContreOffre.submit()}
        okText={t('workflow.contreOffre.enregistrer')}
        okButtonProps={{ style: { background: G, borderColor: G }, loading: saving }}
        cancelText={t('workflow.annuler')}
        width={560}
        destroyOnClose
      >
        <Form form={formContreOffre} layout="vertical" onFinish={onContreOffreSubmit} style={{ marginTop: 8 }}>
          <Alert type="info" showIcon style={{ borderRadius: 8, marginBottom: 12 }}
            message={<span style={{ fontSize: 12 }}>
              <Trans t={t} i18nKey="workflow.contreOffre.offreInitiale" values={{ montant: formatMontant(offre.montantTotalOffre) }} components={{ b: <strong /> }} />
            </span>}
          />
          <Form.Item name="montantContreOffre" label={parCompagnie ? t('workflow.contreOffre.montantCompagnie') : t('workflow.contreOffre.montantHomologue')}
            rules={[{ required: true, message: t('workflow.montantRequis') }]}>
            <CBInputNumber min={0} style={{ width: '100%' }} addonAfter="FCFA"
              formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
              parser={v => v.replace(/\s/g, '')}
            />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="dateReponse" label={t('workflow.contreOffre.dateReception')} initialValue={dayjs()}>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label={t('workflow.contreOffre.observations')}>
            <Input.TextArea rows={3} placeholder={parCompagnie ? t('workflow.contreOffre.justificationsCompagnie') : t('workflow.contreOffre.justificationsHomologue')} />
          </Form.Item>
          <Divider style={{ fontSize: 12 }}>{t('workflow.contreOffre.documentRecu')}</Divider>
          <GedFileField
            value={fichierContreOffre}
            onChange={setFichierContreOffre}
            maxSizeMo={10}
            hint={<Trans t={t} i18nKey="workflow.contreOffre.aideDocument" components={{ b: <strong /> }} />}
          />
        </Form>
      </Modal>

      {/* ═══ Modal mail victime ═══ */}
      <ModalEnvoyerMail
        open={mailVictimeOpen}
        onClose={() => setMailVictimeOpen(false)}
        onSent={onMailVictimeSent}
        titre={t('workflow.mail.titreVictime', { nom: victime.nom, prenoms: victime.prenoms })}
        titreDocument={tDocument(langueVict)('offre.lettre.titres.offreVictime', { victime: `${victime.nom} ${victime.prenoms}` })}
        langue={langueVict}
        destinataireEmail={emailVictime}
        sinistre={sinistre}
        nature="OFFRE_INDEMNISATION"
        loading={saving}
        documentBlob={genererPdfOffre(victime, sinistre, offre, 'victime', expertise, ayantsDroit, null, langueVict)}
        nomFichier={`Offre_${sinistre?.numeroSinistreLocal || 'indemnisation'}_${victime.nom}.pdf`}
        sujetInitial={genMailContent('victime', mailVictimeRelance).sujet}
        corpsInitial={genMailContent('victime', mailVictimeRelance).corps}
      />

      {/* ═══ Modal des étapes TE (chèque, transmission, quittance) ═══ */}
      <Modal
        open={!!etapeTe}
        title={ETAPES_TE[etapeTe]?.titre}
        onCancel={() => { setEtapeTe(null); formEtapeTe.resetFields(); setFichierEtapeTe(null); }}
        onOk={() => formEtapeTe.submit()}
        okText={ETAPES_TE[etapeTe]?.ok}
        okButtonProps={{ style: { background: G, borderColor: G }, loading: saving }}
        cancelText={t('workflow.annuler')}
        width={520}
        destroyOnClose
      >
        <Form form={formEtapeTe} layout="vertical" onFinish={onEtapeTeSubmit} style={{ marginTop: 8 }}
          initialValues={{ date: dayjs(), montant: etapeTe === 'CHEQUE' ? (montantTransmis ?? undefined) : undefined }}>
          <Form.Item name="date" label={ETAPES_TE[etapeTe]?.date} rules={[{ required: true, message: t('workflow.dateRequise') }]}>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabledDate={d => d && d > dayjs().endOf('day')} />
          </Form.Item>
          {ETAPES_TE[etapeTe]?.cheque && (
            <Row gutter={12}>
              <Col span={14}>
                <Form.Item name="montant" label={t('workflow.te.montantCheque')} rules={[{ required: true, message: t('workflow.montantRequis') }]}>
                  <CBInputNumber min={0} style={{ width: '100%' }} addonAfter="FCFA"
                    formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} parser={v => v.replace(/\s/g, '')} />
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item name="reference" label={t('workflow.te.numeroCheque')}>
                  <Input maxLength={60} />
                </Form.Item>
              </Col>
            </Row>
          )}
          {ETAPES_TE[etapeTe]?.doc && (
            <>
              <Divider style={{ fontSize: 12 }}>{t('workflow.te.recommande', { doc: ETAPES_TE[etapeTe].doc })}</Divider>
              <GedFileField value={fichierEtapeTe} onChange={setFichierEtapeTe} maxSizeMo={10} />
            </>
          )}
        </Form>
      </Modal>

      {/* ═══ Modal accord victime ═══ */}
      <Modal
        open={accordOpen}
        title={<Space><CheckCircleOutlined style={{ color: G }} />{t('workflow.accord.titre')}</Space>}
        onCancel={() => { setAccordOpen(false); formAccord.resetFields(); setFichierAccord(null); }}
        onOk={() => formAccord.submit()}
        okText={t('workflow.accord.enregistrer')}
        okButtonProps={{ style: { background: G, borderColor: G }, loading: saving }}
        cancelText={t('workflow.annuler')}
        width={520}
        destroyOnClose
      >
        <Form form={formAccord} layout="vertical" onFinish={onAccordSubmit} style={{ marginTop: 8 }}>
          <Alert type="success" showIcon style={{ borderRadius: 8, marginBottom: 12 }}
            message={<span style={{ fontSize: 12 }}>
              <Trans t={t} i18nKey="workflow.accord.montantAccorde" values={{ montant: formatMontant(montantTransmis) }} components={{ b: <strong /> }} />
              {te
                ? <>{' - '}{t('workflow.accord.compagnieRegle')}</>
                : <>{' - '}<Trans t={t} i18nKey="workflow.accord.sousDossierBap" components={{ b: <strong /> }} /></>}
            </span>}
          />
          <Form.Item name="dateAccord" label={t('workflow.accord.dateReception')}
            initialValue={dayjs()} rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="observations" label={t('workflow.accord.observations')}>
            <Input.TextArea rows={2} placeholder={t('workflow.accord.notes')} />
          </Form.Item>
          <Divider style={{ fontSize: 12 }}>{t('workflow.accord.courrierSigne')}</Divider>
          <GedFileField
            value={fichierAccord}
            onChange={setFichierAccord}
            maxSizeMo={10}
            hint={<Trans t={t} i18nKey="workflow.accord.aideDocument" components={{ b: <strong /> }} />}
          />
        </Form>
      </Modal>
    </div>
  );
}
